import React from 'react'
import DotChart from '../../../../components/dot-chart'
import {
    Table,
    Column as RSuiteColumn,
    ColumnGroup,
    HeaderCell,
    Cell,
} from "rsuite-table";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tooltip, Whisper } from 'rsuite';
import { formatNumberwithCommas } from '../../../../utils/formatter';
import ProductInfoIcon from '../../../../assets/info-circle-grey.svg';



type SupplierVal = { district: string; value: number | null };
type SuppliersPctItem = { atkore?: SupplierVal[]; zekelman?: SupplierVal[]; nucor?: SupplierVal[] };

type TableDataProps = {
    chartData: any[],
    tableData: {
        monthlyTL: SupplierVal[];
        suppliersPct: SuppliersPctItem[] | undefined;
        vendors: string[]
    };
};

type DistrictComparisonProps = {
    data: TableDataProps;
    baseSku: string;
    date: string;
    isShowDotValue: boolean
}

const DistrictComparison = ({ data, baseSku, date, isShowDotValue }: DistrictComparisonProps) => {

    const districts = data?.tableData?.monthlyTL?.map(d => d.district) ?? [];

    // find array for vendor key in suppliersPct
    const getSupplierArray = (key: string): SupplierVal[] => {
        for (const obj of data?.tableData?.suppliersPct ?? []) {
            const arr = (obj as Record<string, SupplierVal[]>)[key];
            if (arr) return arr;
        }
        return [];
    };

    // Extract vendor names from suppliersPct keys
    // Extract vendor names from suppliersPct keys (lowercase)
    const vendors = Array.from(
        new Set(
            (data?.tableData?.suppliersPct ?? []).flatMap(obj =>
                Object.keys(obj).map(k => k.toLowerCase())
            )
        )
    );

    const vendorsForGraph = Array.from(
        new Set(
            data?.chartData?.flatMap(row => Object.keys(row).filter(k => k !== 'district'))
        )
    );
    // dynamic rows from vendors meta
    const vendorRows = (vendors ?? []).map(v => ({
        name: v,
        values: getSupplierArray(v.toUpperCase()).map(x => x.value),
    }));

    const tableRows: Array<Record<string, any>> = (() => {
        const rows: Array<Record<string, any>> = [];

        // Monthly TL row
        const monthlyRow: Record<string, any> = {
            label: 'Monthly Avg. TL',
            rowType: 'monthlyTL'
        };
        (data?.tableData?.monthlyTL ?? []).forEach(v => {
            monthlyRow[v.district] = v.value;
        });
        rows.push(monthlyRow);

        // Vendor rows
        vendorRows.forEach(r => {
            const row: Record<string, any> = {
                label: r.name.toUpperCase(),
                rowType: 'vendor'
            };
            districts.forEach((d, i) => {
                row[d] = r.values?.[i] ?? null;
            });
            rows.push(row);
        });

        return rows;
    })();

    // Compute per-district min/max for vendor percentages
    const vendorStatsByDistrict: Record<string, { min: number; max: number }> = {};
    districts.forEach((d, i) => {
        const vals = vendorRows
            .map(r => Number(r.values?.[i]))
            .filter(v => Number.isFinite(v) && v > 0);
        const min = vals.length ? Math.min(...vals) : 0;
        const max = vals.length ? Math.max(...vals) : 0;
        vendorStatsByDistrict[d] = { min, max };
    });


    // Compute global min/max for monthly TL values
    const monthlyVals = (data?.tableData?.monthlyTL ?? [])
        .map(v => Number(v.value))
        .filter(v => Number.isFinite(v) && v > 0);
    const monthlyStats = monthlyVals.length
        ? { min: Math.min(...monthlyVals), max: Math.max(...monthlyVals) }
        : undefined;

    const cellStyle = (val: any, stats?: { min: number; max: number }) => {
        const n = Number(val);
        if (!Number.isFinite(n) || !stats || stats.max <= stats.min) return {};
        const norm = Math.max(0, Math.min(1, (n - stats.min) / (stats.max - stats.min))); // 0..1
        const lightness = 95 - norm * 55; // 95% (light) -> 40% (heavy green)
        const bg = `hsl(140, 70%, ${lightness}%)`;
        const color = lightness < 60 ? '#ffffff' : '#111827';
        return { backgroundColor: bg, color, fontWeight: 600, height: "100%", width: "100%" };
    };

    return (
        <div className='w-full overflow-hidden flex flex-col gap-3'>
            <div className='px-5 w-[79vw] flex flex-col shadow-module-card border border-[#bebebe] bg-white p-pd13 text-fs16 rounded-md'>
                <DotChart data={data?.chartData} vendors={vendorsForGraph} baseSku={baseSku} date={date} isShowDotValue={isShowDotValue} />
            </div >
            {/* Give the table a fixed height and enable both scrolls */}
            <div className='w-[79vw] h-[60vh] overflow-auto'>
                <DataTable
                    className="w-full h-full"
                    value={tableRows}
                    scrollable
                    scrollHeight='flex'
                    rowClassName={(rowData) => rowData?.rowType === 'section' ? 'bg-[#F9FAFB]' : ''}
                >
                    <Column
                        frozen
                        field="label"
                        header={
                            <span className="flex items-center gap-1">
                                <span>Market Share</span>
                                <Whisper
                                    trigger="hover"
                                    placement="top"
                                    speaker={<Tooltip>Trailing 3 Month</Tooltip>}
                                >
                                    <img
                                        src={ProductInfoIcon}
                                        alt="info"
                                        className="h-[1.6vh] w-[1.6vh] cursor-pointer"
                                    />
                                </Whisper>
                            </span>
                        }
                        headerStyle={{ fontStyle: "italic", fontWeight: "500" }}
                        style={{ width: '200px', textAlign: 'left' }}
                        body={(rowData) => (
                            <span className={(rowData.rowType === 'section' || rowData.rowType === 'monthlyTL') ? 'italic text-[#374151]' : ''}>
                                <b>{rowData.label}</b>
                            </span>
                        )}
                    />
                    {districts.map((d) => (
                        <Column
                            key={d}
                            field={d}
                            header={d}
                            headerClassName="district-comparison-text"
                            style={{ width: '100px', textAlign: 'center', margin: 'auto' }}
                            body={(rowData) => {
                                const val = rowData[d];
                                if (rowData.rowType === 'section') return '';
                                if (val == null || val === '' || val === 0) return '-';
                                const stats = rowData.rowType === 'vendor' ? vendorStatsByDistrict[d] : monthlyStats;
                                const style = cellStyle(val, stats);
                                console.log(style, "style")
                                const display = rowData.rowType === 'vendor' ? `${Math.round(Number(val))}%` : `${formatNumberwithCommas(Math.round(val / 40000), false)}`;
                                return <span>{display}</span>;
                            }}
                        />
                    ))}
                </DataTable>
            </div >
        </div >
    )
}

export default DistrictComparison